// BloodFlow × EMR — tailored clinical scenarios + EMR registry.
// Each note streams line-by-line. Content authored to be medically plausible
// and varied across specialties to show BloodFlow works inside every system.

const cite = (t) => `<span class="bf-cite">${t}</span>`;

export const SCENARIOS = {
  // ------------------------------------------------------------------ EPIC
  epic: {
    banner: { sub: 'Oncology · case contextualization · v2026.02.11', version: 'BloodFlow · 09:14' },
    meta: [
      { k: 'Patient', v: 'A. Marsh · 36F' },
      { k: 'Diagnosis', v: 'Inv. carcinoma L breast' },
      { k: 'Stage / status', v: 'cT3 N1 M0 · HER2+ HR−' },
      { k: 'Written', v: '2026-02-11 · 09:14' },
    ],
    sources: 17,
    sections: [
      { h: 'Oncological diagnosis', ix: '2025-08-11', rows: [
        { lbl: 'Histology', val: 'Invasive carcinoma of the left breast (NST)' },
        { lbl: 'Stage', val: 'cT3 N1 (N3b? on PET) M0' },
        { lbl: 'Molecular', val: `HER2+ / HR− ${cite('IHC 2+')} ${cite('D-DISH 2.4')}` },
        { lbl: 'Ki-67 (IHC)', val: `<span class="hi">80%</span> · 2025-08-14` },
      ]},
      { h: 'Therapy timeline', rows: [
        { lbl: 'Neoadjuvant — AC', val: `Doxorubicin + Cyclophosphamide · 4 cycles · ended 2025-11-17 ${cite('complete')}` },
        { lbl: 'Current', val: `Weekly Paclitaxel + Pertuzumab + Trastuzumab · since 2025-12-05 ${cite('ongoing')}` },
        { lbl: 'Markers', val: 'Clip in breast lesion ≈5 cm · clip in L axillary node 31×18 mm' },
      ]},
      { h: 'Laboratory', ix: '2026-02-11', rows: [
        { lbl: 'Hemoglobin', val: `<span class="lo">10.4 g/dL</span> <span class="mut">(low — G1 anemia)</span>` },
        { lbl: 'Neutrophils (abs)', val: `4.93 ×10⁹/L · <span class="ok">no neutropenia</span>` },
        { lbl: 'LVEF', val: `56% · 2025-10-30 ${cite('within range, anti-HER2')}` },
        { lbl: 'CA 15-3', val: `49 U/mL · 2025-08-20 ${cite('single value')}` },
      ]},
      { h: 'Interpretation', p: `Patient on neoadjuvant treatment (post-AC) with weekly paclitaxel + pertuzumab + trastuzumab, consistent with strategies for locally advanced HER2-positive carcinoma ${cite('NCCN BC v3.2025')}. LVEF 56% supports continuation of anti-HER2 therapy ${cite('ESC 2022 §7.4')}.` },
      { h: 'Points of attention', list: { type: 'att', items: [
        'Mild anemia (Hb 10.4) — recheck before next cycle',
        'No serial cardiac monitoring during anti-HER2 therapy',
        'No imaging or CA 15-3 trend to assess tumor response',
      ]}},
      { h: 'Missing data — proposed orders', list: { type: 'miss', items: [
        'Mid-treatment imaging (US / PET-CT / MRI) for response',
        'Serial LVEF / NT-proBNP for cardiac surveillance',
        'Hb / Hct trend prior to next paclitaxel cycle',
      ]}},
    ],
  },

  // ---------------------------------------------------------------- CERNER
  cerner: {
    banner: { sub: 'Inpatient medicine · case contextualization · v2026.02.11', version: 'BloodFlow · 06:50' },
    meta: [
      { k: 'Patient', v: 'R. Okafor · 68F' },
      { k: 'Admission', v: 'CAP · day 2' },
      { k: 'Bed', v: 'MS4A · 401-01' },
      { k: 'Written', v: '2026-02-11 · 06:50' },
    ],
    sources: 12,
    sections: [
      { h: 'Active problems', rows: [
        { lbl: 'Primary', val: 'Community-acquired pneumonia (RLL consolidation)' },
        { lbl: 'Comorbid', val: 'AKI stage 1 on CKD 3a · T2DM · HTN' },
        { lbl: 'Allergies', val: `<span class="lo">Codeine, Penicillin</span> ${cite('chart')}` },
      ]},
      { h: 'Trajectory — last 28h', rows: [
        { lbl: 'Resp rate', val: `<span class="ok">18 → 12</span> /min · improving` },
        { lbl: 'SpO₂', val: `98 → 99% on 2L · weaning O₂` },
        { lbl: 'Temp', val: `97.6 → 98.2 °F · afebrile 24h` },
        { lbl: 'BP', val: `132/79 → 120/70 mmHg` },
      ]},
      { h: 'Laboratory', ix: '2026-02-11', rows: [
        { lbl: 'Creatinine', val: `<span class="lo">1.6 mg/dL</span> (baseline 1.1) <span class="mut">— AKI not resolved</span>` },
        { lbl: 'Hemoglobin', val: `14.8 g/dL · stable` },
        { lbl: 'WBC', val: `<span class="ok">11.2 → 8.9 ×10⁹/L</span> · trending down` },
        { lbl: 'CO₂', val: `<span class="lo">18 mmol/L</span> ${cite('mild metabolic acidosis')}` },
      ]},
      { h: 'Interpretation', p: `Clinically improving CAP on IV antibiotics; vitals and WBC trending toward resolution ${cite('IDSA/ATS 2019')}. AKI persists with rising creatinine — review nephrotoxic agents and fluid balance before continuing the current regimen ${cite('KDIGO AKI 2012')}.` },
      { h: 'Points of attention', list: { type: 'att', items: [
        'AKI not resolving — hold/renally dose nephrotoxic drugs',
        'Low CO₂ — confirm acid–base status with venous gas',
        'IV-to-oral antibiotic switch criteria nearly met',
      ]}},
      { h: 'Missing data — proposed orders', list: { type: 'miss', items: [
        'Repeat BMP in 12h to trend creatinine',
        'Urine output / fluid balance charting',
        'Procalcitonin to support antibiotic de-escalation',
      ]}},
    ],
  },

  // ----------------------------------------------------------------- GLINTT
  glintt: {
    banner: { sub: 'Post-op orthopedics · case contextualization · v2026.02.11', version: 'BloodFlow · 08:05' },
    meta: [
      { k: 'Patient', v: 'J. Durão · 77F' },
      { k: 'Procedure', v: 'L hip ORIF · day 10' },
      { k: 'Diagnosis', v: 'Femoral neck fracture' },
      { k: 'Written', v: '2026-02-11 · 08:05' },
    ],
    sources: 9,
    sections: [
      { h: 'Episode overview', rows: [
        { lbl: 'Admission', val: 'Fall · femoral neck fracture · ORIF 2026-02-01' },
        { lbl: 'Status', val: 'Day 10 · rehabilitation · mobilising with aid' },
        { lbl: 'Allergies', val: `<span class="lo">Cow's milk protein · Penicillin</span>` },
        { lbl: 'Special care', val: 'High falls risk' },
      ]},
      { h: 'Hemodynamic stability — 48h', rows: [
        { lbl: 'Heart rate', val: `78 → 82 bpm · stable` },
        { lbl: 'SpO₂', val: `<span class="ok">96 → 98%</span>` },
        { lbl: 'Temp', val: `36.8 °C · afebrile` },
        { lbl: 'NEWS', val: `<span class="hi">2</span> ${cite('decreasing trend')}` },
      ]},
      { h: 'Laboratory', ix: '2026-02-11', rows: [
        { lbl: 'Hemoglobin', val: `<span class="lo">10.6 g/dL</span> <span class="mut">(post-op anemia)</span>` },
        { lbl: 'Leukocytes', val: `<span class="ok">8.4 ×10⁹/L</span> · normal` },
        { lbl: 'Platelets', val: `302 ×10⁹/L` },
        { lbl: 'CRP', val: `<span class="ok">28 → 9 mg/L</span> · falling` },
      ]},
      { h: 'Interpretation', p: `Uncomplicated post-operative course on day 10; inflammatory markers falling and no signs of infection at the surgical site ${cite('NICE NG157')}. Mild post-operative anemia, haemodynamically stable. Continue VTE prophylaxis and structured rehabilitation.` },
      { h: 'Points of attention', list: { type: 'att', items: [
        'Post-op anemia (Hb 10.6) — monitor, no transfusion criteria met',
        'High falls risk — confirm prophylaxis in place',
        'No active wound infection signs',
      ]}},
      { h: 'Missing data — proposed orders', list: { type: 'miss', items: [
        'Hb trend in 48h before discharge planning',
        'Document VTE prophylaxis duration',
        'Bone health / fracture-prevention review',
      ]}},
    ],
  },

  // -------------------------------------------------------------- TRAKCARE
  trakcare: {
    banner: { sub: 'Obstetrics · case contextualization · v2026.02.11', version: 'BloodFlow · 11:21' },
    meta: [
      { k: 'Patient', v: 'A. Jenkins · 28F' },
      { k: 'Service', v: 'Obstetrics · 35+2 wks' },
      { k: 'Diagnosis', v: 'Gestational diabetes' },
      { k: 'Written', v: '2026-02-11 · 11:21' },
    ],
    sources: 10,
    sections: [
      { h: 'Pregnancy overview', rows: [
        { lbl: 'Current', val: 'Single pregnancy · AEDD 2026-03-06 · LMP 2025-06-27' },
        { lbl: 'Problem', val: `Gestational diabetes mellitus since 2025-12-25 ${cite('linked to pregnancy')}` },
        { lbl: 'Plan', val: 'Natural delivery · labour & delivery unit' },
        { lbl: 'Alert', val: `<span class="lo">Active pregnancy / breastfeeding</span>` },
      ]},
      { h: 'Glycaemic control — 14 days', rows: [
        { lbl: 'Fasting glucose', val: `<span class="ok">5.1 mmol/L</span> · within target` },
        { lbl: '1h post-prandial', val: `<span class="lo">7.9 mmol/L</span> ${cite('above 7.8 target')}` },
        { lbl: 'HbA1c', val: `5.6% · 2026-01-20` },
        { lbl: 'Management', val: 'Diet + metformin · no insulin' },
      ]},
      { h: 'Maternal labs', ix: '2026-02-11', rows: [
        { lbl: 'Hemoglobin', val: `<span class="lo">10.8 g/dL</span> <span class="mut">(physiological)</span>` },
        { lbl: 'Platelets', val: `<span class="ok">228 ×10⁹/L</span> · normal` },
        { lbl: 'BP', val: `118/72 mmHg · no pre-eclampsia signs` },
        { lbl: 'Urine protein', val: `<span class="ok">Negative</span>` },
      ]},
      { h: 'Interpretation', p: `Well-controlled GDM on diet and metformin; occasional post-prandial excursions but fasting values at target ${cite('NICE NG3')}. No features of pre-eclampsia. Continue growth surveillance and plan timing of birth per protocol ${cite('FIGO 2015')}.` },
      { h: 'Points of attention', list: { type: 'att', items: [
        'Post-prandial readings above target — review diet/dose',
        'Mild anemia — confirm iron status',
        'Plan delivery timing for GDM (≤ 40+6)',
      ]}},
      { h: 'Missing data — proposed orders', list: { type: 'miss', items: [
        'Growth scan + liquor volume this week',
        'Ferritin / iron studies',
        'Anaesthetic review for delivery planning',
      ]}},
    ],
  },

  // ----------------------------------------------------------------- ORBIS
  orbis: {
    banner: { sub: 'Peri-operative · case contextualization · v2026.02.11', version: 'BloodFlow · 16:01' },
    meta: [
      { k: 'Patient', v: 'C. Kelly · 68M' },
      { k: 'Service', v: 'General surgery' },
      { k: 'Plan', v: 'Elective colorectal resection' },
      { k: 'Written', v: '2026-02-11 · 16:01' },
    ],
    sources: 11,
    sections: [
      { h: 'Pre-operative status', rows: [
        { lbl: 'Procedure', val: 'Elective anterior resection · scheduled +3 days' },
        { lbl: 'Comorbid', val: 'T2DM · prior pacemaker' },
        { lbl: 'Allergies', val: `<span class="lo">Shellfish</span> · moderate` },
        { lbl: 'Lines', val: 'Arterial catheter · 53 days in situ' },
      ]},
      { h: 'Medication review', rows: [
        { lbl: 'Metformin', val: `500 mg BD ${cite('hold per peri-op protocol')}` },
        { lbl: 'Anticoagulation', val: `<span class="lo">On DOAC</span> — bridging plan not documented` },
        { lbl: 'Analgesia', val: 'Paracetamol 1 g QDS' },
      ]},
      { h: 'Laboratory', ix: '2026-02-11', rows: [
        { lbl: 'Hemoglobin', val: `<span class="lo">10.9 g/dL</span> <span class="mut">(pre-op anemia)</span>` },
        { lbl: 'Ferritin', val: `<span class="lo">22 µg/L</span> ${cite('iron deficiency')}` },
        { lbl: 'eGFR', val: `<span class="ok">68 mL/min</span>` },
        { lbl: 'HbA1c', val: `7.4% · 2026-01-15` },
      ]},
      { h: 'Interpretation', p: `Iron-deficiency anemia ahead of major colorectal surgery — pre-operative optimisation is indicated to reduce transfusion risk ${cite('NICE NG24')}. DOAC requires a documented peri-operative plan; renal function adequate for timing ${cite('BSH 2016')}.` },
      { h: 'Points of attention', list: { type: 'att', items: [
        'Pre-op iron-deficiency anemia — optimise before surgery',
        'DOAC bridging / cessation plan not documented',
        'Hold metformin per peri-operative protocol',
      ]}},
      { h: 'Missing data — proposed orders', list: { type: 'miss', items: [
        'IV iron + recheck Hb pre-op',
        'Document DOAC stop date & last dose',
        'Group & save · anaesthetic pre-assessment',
      ]}},
    ],
  },

  // ------------------------------------------------------------------ EMIS
  emis: {
    banner: { sub: 'Primary-care lab review · v2026.02.11', version: 'BloodFlow · 09:22' },
    meta: [
      { k: 'Patient', v: 'D. Powell · 61M' },
      { k: 'Encounter', v: 'Results review' },
      { k: 'Problems', v: 'HTN · dyslipidaemia' },
      { k: 'Written', v: '2026-02-11 · 09:22' },
    ],
    sources: 8,
    sections: [
      { h: 'Active problems', rows: [
        { lbl: 'On record', val: 'Hypertension · mixed dyslipidaemia' },
        { lbl: 'Medication', val: 'Amlodipine 5 mg · Atorvastatin 20 mg' },
        { lbl: 'Alerts', val: `<span class="lo">No ethnicity recorded</span> · pneumococcal due` },
      ]},
      { h: 'Results — this review', ix: '2026-02-11', rows: [
        { lbl: 'Total cholesterol', val: `<span class="lo">5.8 mmol/L</span> ${cite('above target')}` },
        { lbl: 'LDL', val: `<span class="lo">3.4 mmol/L</span>` },
        { lbl: 'ALT', val: `<span class="lo">62 U/L</span> <span class="mut">(mildly raised)</span>` },
        { lbl: 'HbA1c', val: `<span class="ok">41 mmol/mol</span> · non-diabetic` },
      ]},
      { h: 'Trend', rows: [
        { lbl: 'BP (avg, home)', val: `<span class="lo">146/88 mmHg</span> · above 135/85` },
        { lbl: 'eGFR', val: `<span class="ok">82 mL/min</span> · stable` },
        { lbl: 'QRISK3', val: `<span class="hi">18.4%</span> 10-yr ${cite('recalculated')}` },
      ]},
      { h: 'Interpretation', p: `LDL above target on atorvastatin 20 mg with persistently raised home BP; statin uptitration and antihypertensive review are indicated given QRISK3 18.4% ${cite('NICE NG28 / NG136')}. Mildly raised ALT — review alcohol and consider NAFLD pathway ${cite('NICE NG49')}.` },
      { h: 'Points of attention', list: { type: 'att', items: [
        'LDL above target — consider statin uptitration',
        'Home BP above 135/85 — review antihypertensive',
        'Raised ALT — repeat + NAFLD assessment',
      ]}},
      { h: 'Missing data — proposed orders', list: { type: 'miss', items: [
        'Repeat LFTs + liver screen',
        'Ambulatory / home BP series',
        'Record ethnicity · pneumococcal vaccination',
      ]}},
    ],
  },

  // -------------------------------------------------------------- SYSTMONE
  systmone: {
    banner: { sub: 'Chronic-disease review · v2026.02.11', version: 'BloodFlow · 13:32' },
    meta: [
      { k: 'Patient', v: 'E. Bavington · 64F' },
      { k: 'Encounter', v: 'Diabetes annual review' },
      { k: 'Problems', v: 'T2DM · CKD 3a' },
      { k: 'Written', v: '2026-02-11 · 13:32' },
    ],
    sources: 9,
    sections: [
      { h: 'Active problems', rows: [
        { lbl: 'Major', val: 'Type 2 diabetes · CKD stage 3a · hypertension' },
        { lbl: 'Medication', val: 'Metformin 1 g BD · Ramipril 5 mg' },
        { lbl: 'Status alerts', val: `<span class="lo">Drug review required</span> · flu vaccine due` },
      ]},
      { h: 'Diabetes & renal — this review', ix: '2026-02-11', rows: [
        { lbl: 'HbA1c', val: `<span class="lo">61 mmol/mol</span> ${cite('above 53 target')}` },
        { lbl: 'eGFR', val: `<span class="lo">52 mL/min</span> <span class="mut">(CKD 3a)</span>` },
        { lbl: 'ACR', val: `<span class="lo">4.2 mg/mmol</span> · microalbuminuria` },
        { lbl: 'BP', val: `<span class="lo">142/84 mmHg</span>` },
      ]},
      { h: 'Other results', rows: [
        { lbl: 'Total cholesterol', val: `4.6 mmol/L` },
        { lbl: 'Potassium', val: `<span class="ok">4.4 mmol/L</span> · normal` },
        { lbl: 'Foot / retinal', val: `<span class="mut">No screen recorded this year</span>` },
      ]},
      { h: 'Interpretation', p: `Sub-optimal glycaemic control (HbA1c 61) with CKD 3a and microalbuminuria — an SGLT2 inhibitor is indicated for combined renal and glycaemic benefit ${cite('NICE NG28')}. BP above 140/80 target for diabetic CKD; optimise ACE-inhibitor ${cite('NICE NG203')}.` },
      { h: 'Points of attention', list: { type: 'att', items: [
        'HbA1c above target — consider SGLT2 inhibitor',
        'CKD 3a + microalbuminuria — renal protection',
        'BP above 140/80 target',
      ]}},
      { h: 'Missing data — proposed orders', list: { type: 'miss', items: [
        'Annual foot check + retinal screening referral',
        'Repeat ACR to confirm microalbuminuria',
        'Influenza vaccination · medication review',
      ]}},
    ],
  },

  // --------------------------------------------------------------- SCLINICO
  sclinico: {
    banner: { sub: 'Pediatrics · case contextualization · v2026.02.11', version: 'BloodFlow · 12:16' },
    meta: [
      { k: 'Patient', v: 'J. Colaço · 9y M' },
      { k: 'Encounter', v: 'Routine review' },
      { k: 'Problems', v: 'Asthma · growth' },
      { k: 'Written', v: '2026-02-11 · 12:16' },
    ],
    sources: 8,
    sections: [
      { h: 'Visit overview', rows: [
        { lbl: 'Age', val: '9 years · paediatrics' },
        { lbl: 'Background', val: `Hydrocele repair (HDE) 09/2024 · discharged ${cite('resolved')}` },
        { lbl: 'Active follow-up', val: 'Mild persistent asthma · speech-delay review' },
        { lbl: 'Referrals open', val: 'Dermatology · ENT · Urology' },
      ]},
      { h: 'Growth & development', rows: [
        { lbl: 'Weight', val: `28 kg · <span class="hi">P95</span> (stable)` },
        { lbl: 'Height', val: `118 cm · P75` },
        { lbl: 'Development', val: `<span class="lo">Mild speech delay</span> ${cite('age-appropriate motor')}` },
      ]},
      { h: 'Examination & vitals', rows: [
        { lbl: 'BP', val: `107/60 mmHg · normal for age` },
        { lbl: 'Heart rate', val: `65 bpm` },
        { lbl: 'Skin', val: `Haemangioma-type lesion · L forearm ${cite('monitor')}` },
      ]},
      { h: 'Interpretation', p: `Well child on routine paediatric review; growth tracking on centiles with weight at P95 — reinforce diet and activity ${cite('WHO growth 2007')}. Mild speech delay warrants continued developmental follow-up; asthma stable on current plan.` },
      { h: 'Points of attention', list: { type: 'att', items: [
        'Weight at P95 — dietary advice, monitor BMI trend',
        'Speech delay — confirm SLT follow-up booked',
        'Forearm skin lesion — dermatology review pending',
      ]}},
      { h: 'Missing data — proposed orders', list: { type: 'miss', items: [
        'Document asthma control (symptom score / inhaler technique)',
        'Speech & language therapy outcome',
        'Dermatology referral status',
      ]}},
    ],
  },
};

export const EMRS = [
  { id: 'epic',     name: 'Epic',          vendor: 'EpicCare',    mark: 'Ep', markBg: '#9b1c31' },
  { id: 'cerner',   name: 'Oracle Cerner', vendor: 'PowerChart',  mark: 'Or', markBg: '#2566ad' },
  { id: 'glintt',   name: 'Glintt',        vendor: 'Mozy Portal', mark: 'Gl', markBg: '#0a9d9d' },
  { id: 'trakcare', name: 'InterSystems',  vendor: 'TrakCare',    mark: 'Tc', markBg: '#0a7d6e' },
  { id: 'orbis',    name: 'Dedalus',       vendor: 'ORBIS',       mark: 'Db', markBg: '#1666e6' },
  { id: 'emis',     name: 'EMIS Web',      vendor: 'Health Care', mark: 'Em', markBg: '#e07b00' },
  { id: 'systmone', name: 'TPP',           vendor: 'SystmOne',    mark: 'S1', markBg: '#1f6fb5' },
  { id: 'sclinico', name: 'SClínico',      vendor: 'SPMS',        mark: 'Sc', markBg: '#3a3a3a' },
];

// "Before BloodFlow" — the rushed, unstructured note a clinician types after
// manually hunting data across the EMR. Intentionally cramped & low-quality.
export const MESSY = {
  epic: `36F L breast ca HER2+ ?HR-. on wkly tax+pertuz+trastuz since dec, prev AC x4 done ~nov. Hb ~10 low - anaemia? chk. neut ok. LVEF 56 (oct) prob ok. no recent imaging?? ca15-3 49 but old (aug). cont tx. ?repeat echo ?bloods b4 next cycle. pt anxious. f/u`,
  cerner: `68F CAP day2 on IV abx. improving - RR down, afebrile 24h. creat 1.6 (was 1.1) - AKI not better?? watch fluids/nephrotox. wbc trending down ok. co2 18 low - acidosis? ?gas. ?switch IV->PO soon. cont abx, repeat bloods, monitor UO. allergies codeine/pen`,
  glintt: `77F #NOF ORIF day10 rehab. obs stable NEWS2. Hb 10.6 post-op anaemia - no tx? CRP down 9 ok. mobilising w/ aid, falls risk. VTE proph cont. ?Hb recheck b4 d/c. ?bone health rv. allergies milk/pen`,
  trakcare: `28F 35+2 GDM diet+metformin. fasting ok 5.1, 1h pp 7.9 high. hba1c 5.6. bp ok no PET. Hb 10.8 ?iron. cont metformin, growth scan this wk, plan delivery timing. ?ferritin. anaesthetic rv`,
  orbis: `68M elective ant resection +3d. T2DM, pacemaker. Hb 10.9 low, ferritin 22 - iron def anaemia b4 big op!! DOAC - bridging plan?? not documented anywhere. hold metformin periop. eGFR 68 ok. hba1c 7.4. ?IV iron ?G&S. anaesthetic preassess`,
  emis: `61M HTN+lipids rv. chol 5.8 high LDL 3.4. on amlo 5 + atorva 20. ALT 62 mildly up ?alcohol ?NAFLD. hba1c 41 ok. home BP 146/88 high. QRISK ~18%. ?uptitrate statin ?review BP. repeat LFTs. ethnicity not recorded`,
  systmone: `64F T2DM CKD3a annual rv. hba1c 61 high (tgt 53). egfr 52, ACR 4.2 microalb. bp 142/84 high for DM/CKD. on metformin+ramipril. ?add SGLT2i (renal+glyc). K 4.4 ok. chol 4.6. no foot/retinal screen this yr!! flu jab due. med rv`,
  sclinico: `9yo M routine. mild asthma. prev hydrocele surg HDE sep24 d/c. Wt P95 Ht P75. speech delay - SLT? BP 107/60 HR65 nl. haemangioma L forearm - derm. wt P95 diet advice. derm/ENT/uro refs pending. ?asthma control ?SLT f/u`,
};

// data sources the clinician must open manually, one by one (Before state)
export const HUNT_SOURCES = ['Labs / results', 'Medication list', 'Imaging · PACS', 'Previous notes', 'Allergies', 'Vital signs', 'Problem list', 'Discharge letters'];
